// Utilitários de validação e formatação

export const formatCPF = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
  if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
  return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
};

export const formatPhone = (value: string): string => {
  const numbers = value.replace(/\D/g, '');

  // Com DDI (ex: +55 11 99999-9999) - detecta quando começa com código país (mais de 11 dígitos)
  if (numbers.length > 11) {
    const ddi = numbers.slice(0, numbers.length - 11);
    const rest = numbers.slice(numbers.length - 11);
    // Limitar a DDI máximo 3 dígitos + 11 dígitos do número = 14
    const limitedDdi = ddi.slice(0, 3);
    const limitedRest = rest.slice(0, 11);
    if (limitedRest.length <= 2) return `+${limitedDdi} ${limitedRest}`;
    if (limitedRest.length <= 7) return `+${limitedDdi} (${limitedRest.slice(0, 2)}) ${limitedRest.slice(2)}`;
    return `+${limitedDdi} (${limitedRest.slice(0, 2)}) ${limitedRest.slice(2, 7)}-${limitedRest.slice(7, 11)}`;
  }

  // Sem DDI - formato brasileiro padrão
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 6) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

export const validateCPF = (cpf: string): boolean => {
  const numbers = cpf.replace(/\D/g, '');
  
  if (numbers.length !== 11) return false;
  
  // CPFs com todos os dígitos iguais são inválidos
  if (/^(\d)\1{10}$/.test(numbers)) return false;
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(numbers.charAt(i)) * (10 - i);
  }
  const digit1 = 11 - (sum % 11) >= 10 ? 0 : 11 - (sum % 11);
  if (digit1 !== parseInt(numbers.charAt(9))) return false;
  
  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(numbers.charAt(i)) * (11 - i);
  }
  const digit2 = 11 - (sum % 11) >= 10 ? 0 : 11 - (sum % 11);
  if (digit2 !== parseInt(numbers.charAt(10))) return false;
  
  return true;
};

export const validatePhone = (phone: string): boolean => {
  const numbers = phone.replace(/\D/g, '');
  
  // Aceita números com 10 ou 11 dígitos (com ou sem nono dígito do celular)
  if (numbers.length < 10 || numbers.length > 11) return false;
  
  // Verifica se o DDD é válido (11 a 99)
  const ddd = parseInt(numbers.slice(0, 2));
  if (ddd < 11 || ddd > 99) return false;
  
  return true;
};

export const sanitizeCPF = (cpf: string): string => {
  return cpf.replace(/\D/g, '');
};

export const sanitizePhone = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

// ==================== CNPJ Functions ====================

export const formatCNPJ = (value: string): string => {
  const numbers = value.replace(/\D/g, '');

  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
  if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
  if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
  return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
};

export const validateCNPJ = (cnpj: string): boolean => {
  const numbers = cnpj.replace(/\D/g, '');

  if (numbers.length !== 14) return false;

  // CNPJs com todos os dígitos iguais são inválidos
  if (/^(\d)\1{13}$/.test(numbers)) return false;

  // Validação do primeiro dígito verificador
  let sum = 0;
  let pos = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(numbers.charAt(i)) * pos;
    pos = pos === 2 ? 9 : pos - 1;
  }
  const digit1 = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (digit1 !== parseInt(numbers.charAt(12))) return false;

  // Validação do segundo dígito verificador
  sum = 0;
  pos = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(numbers.charAt(i)) * pos;
    pos = pos === 2 ? 9 : pos - 1;
  }
  const digit2 = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (digit2 !== parseInt(numbers.charAt(13))) return false;

  return true;
};

export const sanitizeCNPJ = (cnpj: string): string => {
  return cnpj.replace(/\D/g, '');
};

// ==================== Generic CPF/CNPJ Functions ====================

/**
 * Formata automaticamente CPF ou CNPJ baseado no tamanho
 */
export const formatCPFOrCNPJ = (value: string): string => {
  const numbers = value.replace(/\D/g, '');

  // Se tem mais de 11 dígitos, trata como CNPJ
  if (numbers.length > 11) {
    return formatCNPJ(value);
  }

  // Caso contrário, trata como CPF
  return formatCPF(value);
};

/**
 * Valida automaticamente CPF ou CNPJ baseado no tamanho
 */
export const validateCPFOrCNPJ = (value: string): boolean => {
  const numbers = value.replace(/\D/g, '');

  // Vazio é válido (campo opcional)
  if (numbers.length === 0) return true;

  // Se tem 11 dígitos, valida como CPF
  if (numbers.length === 11) {
    return validateCPF(value);
  }

  // Se tem 14 dígitos, valida como CNPJ
  if (numbers.length === 14) {
    return validateCNPJ(value);
  }

  // Qualquer outro tamanho é inválido
  return false;
};

/**
 * Remove formatação de CPF ou CNPJ
 */
export const sanitizeCPFOrCNPJ = (value: string): string => {
  return value.replace(/\D/g, '');
};